<template>
  <div id="statistics">
    <hr>

    <div class="middle">
      <div class="chart">
        <div class="title">{{$t(`stats.${period || 'all'}`)}}</div>
        <line-chart :data="lineChart.data" :colors="lineChart.colors" :suffix="lineChartSuffix" :prefix="lineChartPrefix" :library="lineChart.chartJsOptions"/>
        <div class="columns" style="margin-top: 20px">
          <div class="column">
            <div class="group">
              <input id="chk_subtotal" type="radio" value="subTotal" v-model="lineChart.showChart">
              <label for="chk_subtotal">{{$t('total')}}</label>
            </div>
            <div class="group">
              <input id="chk_show_last" type="checkbox" v-model="lineChart.showPrevious">
              <label for="chk_show_last">{{$t('stats.showLastYear')}}</label>
            </div>
          </div>
          <div class="column">
            <div class="group">
              <input id="chk_nb_sales" type="radio" value="nbSales" v-model="lineChart.showChart">
              <label for="chk_nb_sales">{{$t('stats.nbSales')}}</label>
            </div>
          </div>
          <div class="column">
            <div class="group">
              <input id="chk_avg_total" type="radio" value="avgTotal" v-model="lineChart.showChart">
              <label for="chk_avg_total">{{$t('stats.avgTotal')}}</label>
            </div>
          </div>
          <div class="column">
            <div class="group">
              <input id="chk_tip_total" type="radio" value="tipTotal" v-model="lineChart.showChart">
              <label for="chk_tip_total">{{$t('stats.tipTotal')}}</label>
            </div>
          </div>
        </div>
        </div>
    </div>

    <hr>
    <div class="middle">
      <div class="chart">
        <div class="title">{{$t("stats.average_invoice")}}</div>
        <bar-chart :data="averageByApp" :colors="colors" :suffix="lineChartSuffix" :prefix="lineChartPrefix"/>
      </div>
    </div>

    <hr>
    <div class="middle">
      <div class="chart">
        <div class="title">{{$t("stats.category")}}</div>
        <bar-chart :data="totalByCategories"/>
      </div>
    </div>

    <hr>
    <div class="middle" v-show="false">
      <div class="chart">
        <div class="title">{{$t("stats.product")}}</div>
        <bar-chart :data="totalByProducts" :colors="colors"/>
      </div>
    </div>

  </div>
</template>

<script>
  import { Core } from '@/core/Core';
  import moment from 'moment-timezone/index';

  export default {
    name: "TodayStats",
    props: ['period'],
    data() {
      return {
        lineChart: {
          showPrevious: true,
          showChart: null,
          data: [],
          colors: [],
          suffix: " $",
          chartJsOptions: {
            legend: {
              labels: {
                filter: this.filterOutPreviousLegend
              }
            }
          },
        },
        totalsCurrent: {
          nbSales: [],
          avgTotal: [],
          subTotal: [],
          tipTotal: [],
        },
        totalsPrevious: {
          nbSales: [],
          avgTotal: [],
          subTotal: [],
          tipTotal: [],
        },
        averageByApp: [],
        totalByCategories: [],
        totalByProducts: [],
        colors: [
          "#e6194B", "#3cb44b", "#ffe119", "#4363d8", "#f58231",
          "#911eb4", "#42d4f4", "#f032e6", "#bfef45", "#fabebe",
          "#469990", "#e6beff", "#9A6324", "#fffac8", "#800000",
          "#aaffc3", "#808000", "#ffd8b1", "#000075", "#a9a9a9",
        ]
      }
    },

    mounted() {
      this.fetchData()
    },

    methods: {
      getTranslatedName(obj) {
        if (!obj) {
          return;
        }
        let locale = this.$ts.state.locale;
        return Object.keys(obj).length === 0
          ? null
          : obj[locale] || obj[Object.keys(obj)[0]]
      },

      lowestKey(obj) {
        return Math.min(...Object.keys(obj).map(e => parseInt(e)))
      },

      highestKey(obj) {
        return Math.max(...Object.keys(obj).map(e => parseInt(e)))
      },


      fetchData() {
        showSpinner();
        let period = this.period || 'all';
        let dateTimeFormat = 'YYYY-MM-DD HH:mm:ss';

        let start = undefined;
        let end = undefined;
        let timeFrameGroupBy = null;
        if (period === 'all') {
          start = undefined;
          end = undefined;
          timeFrameGroupBy = "month";
        }
        else if (period === 'month') {
          start = moment().startOf('month');
          end = moment().endOf('month');
          timeFrameGroupBy = "day";
        }
        else if (period === 'week') {
          start = moment().startOf('week');
          end = moment().endOf('week');
          timeFrameGroupBy = "day";
        }
        else if (period === 'today') {
          start = moment().startOf('day');
          end = moment().endOf('day');
          timeFrameGroupBy = "hour";
        }
        else {
          console.log(`Unkown period name: ${period}`);
          hideSpinner();
          return;
        }

        let options = {
          dataAsObject: true,
          filters: {
            company: this.$route.query.company || undefined,
            branch: this.$route.query.branch && !this.$route.query.company ? this.$route.query.branch : undefined, /* Set branch when present and company is absent */
            start: start ? start.format(dateTimeFormat) : undefined,
            end: end ? end.format(dateTimeFormat) : undefined
          }
        };
        let optionsPrevious = {
          dataAsObject: true,
          filters: {
            company: this.$route.query.company || undefined,
            branch: this.$route.query.branch && !this.$route.query.company ? this.$route.query.branch : undefined, /* Set branch when present and company is absent */
            start: start
              ? moment(start).subtract(1, 'year').endOf(timeFrameGroupBy).format(dateTimeFormat)
              : undefined,
            end: end
              ? moment(end).subtract(1, 'year').endOf(timeFrameGroupBy).format(dateTimeFormat)
              : moment().subtract(1, 'year').endOf(timeFrameGroupBy).format(dateTimeFormat)
          }
        };
        Promise
          .all([
            Core.request("POST", '/api/stats-builder/sales', Object.assign({ groupBy: [timeFrameGroupBy] }, options)),
            Core.request("POST", '/api/stats-builder/sales', Object.assign({ groupBy: [timeFrameGroupBy] }, optionsPrevious)),
            Core.request("POST", '/api/stats-builder/sales', Object.assign({ groupBy: ["appName"], fields: ['avgTotal']  }, options)),
            Core.request("POST", '/api/stats-builder/sales', Object.assign({ groupBy: ["category"], fields: ['subTotal'] }, options)),
            //Core.request("POST", '/api/stats-builder/sales', Object.assign({ groupBy: ["item"], fields: ['subTotal'], order: 'desc' }, options))
          ])
          .then(([byTimeFrame, byTimeFramePrevious, byApp, byCategory, /*byProduct*/]) => {
            hideSpinner();
            /* Set previous time frame data to overlap the current data */
            for (let line of byTimeFramePrevious.data) {
              if (line.day) {
                line.day = (parseInt(line.day.substr(0, 4)) + 1).toString() + line.day.substr(4)
              } else if (line.month) {
                line.month = (parseInt(line.month.substr(0, 4)) + 1).toString() + line.month.substr(4)
              } else if (line.week) {
                line.week = (parseInt(line.week.substr(0, 4)) + 1).toString() + line.week.substr(4)
              }
            }

            /* First graph when grouped by hour  */
            this.totalsCurrent = {
              nbSales: {
                name: 'Nombre de vente',
                data: this.fillMissing(byTimeFrame.data, 'nbSale', timeFrameGroupBy, start, end)
              },
              avgTotal: {
                name: 'Vente moyenne',
                data: this.fillMissing(byTimeFrame.data, 'avgTotal', timeFrameGroupBy, start, end)
              },
              subTotal: {
                name: 'Total des ventes',
                data: this.fillMissing(byTimeFrame.data, 'subTotal', timeFrameGroupBy, start, end)
              },
              tipTotal: {
                name: 'Total des pourboire',
                data: this.fillMissing(byTimeFrame.data, 'tipTotal', timeFrameGroupBy, start, end)
              }
            };

            this.totalsPrevious = {
              nbSales: {
                name: 'Nombre de vente',
                data: this.fillMissing(byTimeFramePrevious.data, 'nbSale', timeFrameGroupBy, start, end)
              },
              avgTotal: {
                name: 'Vente moyenne',
                data: this.fillMissing(byTimeFramePrevious.data, 'avgTotal', timeFrameGroupBy, start, end)
              },
              subTotal: {
                name: 'Total des ventes',
                data: this.fillMissing(byTimeFramePrevious.data, 'subTotal', timeFrameGroupBy, start, end)
              },
              tipTotal: {
                name: 'Total des pourboire',
                data: this.fillMissing(byTimeFramePrevious.data, 'tipTotal', timeFrameGroupBy, start, end)
              }
            };
            /* Set default showed chart to 'subTotal' */
            this.lineChart.showChart = 'subTotal';

            /* Second graph */
            this.averageByApp = [{
              name: 'Average invoice',
              data: byApp.data.map(e => [e.appName, e.avgTotal])
            }];
            /* Third graph */
            this.totalByCategories = [{
              name: 'Sales per categories',
              data: byCategory.data
                .map(e => [this.getTranslatedName(e.categoryName) || '---', e.total])
            }];
            /* Fourth graph */
            /*this.totalByProducts = [{
              name: 'Total',
              data: byProduct.data.slice(0, 20)
                .map(e => [this.getTranslatedName(e.itemName) || ("#" + e.externalId), e.total])
            }, {
              name: 'Quantity',
              data: byProduct.data.slice(0, 20)
                .map(e => [this.getTranslatedName(e.itemName) || ("#" + e.externalId), e.quantity])
            }];*/
          });
      },

      fillMissing(data, fieldName, period, start, end) {
        if (period === 'hour') {
          return this.fillMissingHours(data, fieldName)
        } else if (period === 'day') {
          return this.fillMissingDays(data, fieldName, start, end)
        } else if (period === 'week') {
          return this.fillMissingDays(data, fieldName, start, end)
        } else if (period === 'month') {
          return this.fillMissingMonths(data, fieldName, start, end)
        }
      },

      fillMissingHours(data, fieldName) {
        let totalByHour = Array.from({length: 24}).fill(0);
        for (let line of data) {
          totalByHour[parseInt(line.hour)] = line[fieldName]
        }
        return totalByHour.map((r, hourIndex) => [ window.formatHour(this.hourFormat, hourIndex.toString().padStart(2, '0') + ":00"), r])
      },

      fillMissingDays(data, fieldName, start, end) {
        let dayFormat = 'YYYYMMDD';
        let existingData = data.reduce((obj, e) => {
          obj[e.day] = e[fieldName];
          return obj
        }, {});
        let fromDate = start ? start.clone() : moment(this.lowestKey(existingData), dayFormat);
        let toDate = end ? end.clone() : moment(this.highestKey(existingData), dayFormat);
        let iterDate = fromDate.clone();

        let tmp = [];
        while(toDate.diff(iterDate) >= 0) {
          let key = iterDate.format(dayFormat);
          let value = existingData[key] || "0.00";
          tmp.push([key, value]);
          iterDate.add(1, 'days')
        }
        return tmp;
      },

      fillMissingMonths(data, fieldName, start, end) {
        let dayFormat = 'YYYYMM';
        let existingData = data.reduce((obj, e) => {
          obj[e.month] = e[fieldName];
          return obj
        }, {});
        let fromDate = start ? start.clone() : moment(this.lowestKey(existingData), dayFormat);
        let toDate = end ? end.clone() : moment(this.highestKey(existingData), dayFormat);
        let iterDate = fromDate.clone();

        let tmp = [];
        while(toDate.diff(iterDate) >= 0) {
          let key = iterDate.format(dayFormat);
          let keyFormatted = `${key.substring(4,6)}/${key.substring(2,4)}`;
          let value = existingData[key] || "0.00";
          tmp.push([keyFormatted, value]);
          iterDate.add(1, 'month')
        }
        return tmp;
      },

      filterOutPreviousLegend(legendItem) {
        return this.lineChart.showPrevious
          ? legendItem.datasetIndex % 2 === 0
          : true
      },

      updateGraph() {
        let data = [];
        let colors = [];

        colors.push(this.colors[0]);
        data.push({
          name: this.totalsCurrent[this.lineChart.showChart].name,
          data: this.totalsCurrent[this.lineChart.showChart].data
        });

        if (this.lineChart.showPrevious) {
          colors.push(this.colors[0]);
          data.push({
            name: this.totalsPrevious[this.lineChart.showChart].name,
            data: this.totalsPrevious[this.lineChart.showChart].data,
            dataset: {
              borderDash: [10]
            }
          });
        }
        this.lineChart.data = data;
        this.lineChart.colors = colors;
      }
    },

    watch: {
      '$route': 'fetchData',
      'period': 'fetchData',
      'lineChart.showPrevious'() {
        this.updateGraph()
      },
      'lineChart.showChart'() {
        this.updateGraph()
      },
      totalsCurrent() {
        this.updateGraph()
      }
    },

    computed: {
      lineChartSuffix() {
        if (this.lineChart.showChart === 'nbSales') {
          return "";
        }
        return this.$ts.state.locale === 'fr' ? "$" : ""
      },

      lineChartPrefix() {
        if (this.lineChart.showChart === 'nbSales') {
          return "";
        }
        return this.$ts.state.locale === 'fr' ? "" : "$"
      },

      hourFormat() {
        if (this.$store.state.contextCompany) {
          return this.$store.state.contextCompany.hourFormat;
        }
        return "24";
      }
    }
  }
</script>

<style lang="scss" scoped>

  hr {
    border: 1px dashed #d9d9d9;
    margin: 20px 0px;
  }

  .middle {
    display: flex;
  }

  .chart {
    background-color: white;
    padding: 20px;
    border-radius: 4px;
    flex-grow: 1;
    flex-basis: 0;
    overflow-x: hidden;
    &:first-of-type {
      margin-right: 20px;
    }
    .title {
      font-size: 20px;
      display: flex;
      margin-bottom: 20px;
      font-weight: bold;
      .separator {
        margin: 0 10px;
        font-weight: normal;
        color: #e6e6e6;
      }
      .fade {
        color: #e6e6e6;
        font-weight: normal;
      }
    }
  }

</style>
